import { formatDollars } from '@fruitful/fruitils';
import { buildDraftedMessageAgentPayload } from '@fruitful/llm-service-shared';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { type StyleProp, TextInput, type TextStyle } from 'react-native';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    getDefaultFontFamily,
    H5Regular,
    H6Regular,
    Icon,
    isNonMobileWeb,
    Shadows,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import storage from '../../../common/providers/StorageFacade';

const SAVINGS_TOOLTIP_SEEN_KEY = 'CMM_SAVINGS_GOALS_WIDGET_TOOLTIP_SEEN';
const TOOLTIP_AUTO_DISMISS_MS = 3000;

const EditHintTooltip = ({ children }: { children: React.ReactNode }) => {
    const [visible, setVisible] = useState(() => {
        return storage.getBool(SAVINGS_TOOLTIP_SEEN_KEY) !== true;
    });

    useEffect(() => {
        if (!visible) return undefined;
        const timer = setTimeout(() => {
            setVisible(false);
            storage.setBool(SAVINGS_TOOLTIP_SEEN_KEY, true);
        }, TOOLTIP_AUTO_DISMISS_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [visible]);

    const dismiss = useCallback(() => {
        setVisible(false);
        storage.setBool(SAVINGS_TOOLTIP_SEEN_KEY, true);
    }, []);

    return (
        <YStack width="100%" alignItems="center">
            {visible && (
                <YStack alignItems="center" marginBottom={8} onPress={dismiss}>
                    <XStack
                        backgroundColor="$accent1000"
                        opacity={0.9}
                        borderRadius={8}
                        padding={12}
                        gap={4}
                        alignItems="center"
                        cursor="pointer"
                        onPress={dismiss}
                    >
                        <BodySmallRegular color="$white">
                            Tap any row to edit
                        </BodySmallRegular>
                        <Icon icon={XIcon} size={12} color="$white" />
                    </XStack>
                    <View
                        width={8}
                        height={8}
                        backgroundColor="$accent1000"
                        opacity={0.9}
                        marginTop={-4}
                        style={{ transform: [{ rotate: '45deg' }] }}
                    />
                </YStack>
            )}
            {children}
        </YStack>
    );
};

interface SavingsGoalEstimateItem {
    goalId: string;
    label: string;
    targetAmountCents: number;
}

interface SavingsGoalAmountsWidgetComponentProps {
    goals: SavingsGoalEstimateItem[];
    confirmationPrefix: string;
    confirmationPayload: Record<string, unknown>;
    disabled?: boolean;
    isActive?: boolean;
    onConfirm: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

const formatWithCommas = (digits: string): string => {
    if (digits === '') return '';
    return Number(digits).toLocaleString('en-US');
};

// --- Desktop inline edit cell ---

interface EditableAmountCellProps {
    goal: SavingsGoalEstimateItem;
    editedCents: number;
    isEditing: boolean;
    isDisabled: boolean;
    onStartEdit: () => void;
    onCommit: (newCents: number) => void;
}

const EditableAmountCell = ({
    goal,
    editedCents,
    isEditing,
    isDisabled,
    onStartEdit,
    onCommit,
}: EditableAmountCellProps) => {
    const [draft, setDraft] = useState('');
    const inputRef = useRef<TextInput>(null);

    useEffect(() => {
        if (isEditing) {
            setDraft('');
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [isEditing]);

    const handleStartEdit = useCallback(() => {
        if (isDisabled) return;
        onStartEdit();
    }, [isDisabled, onStartEdit]);

    const handleCommit = useCallback(() => {
        if (draft === '') {
            onCommit(editedCents);
            return;
        }
        const dollars = parseInt(draft, 10);
        if (Number.isNaN(dollars) || dollars <= 0) {
            onCommit(editedCents);
            return;
        }
        onCommit(dollars * 100);
    }, [draft, editedCents, onCommit]);

    const placeholderValue = formatWithCommas(
        Math.round(editedCents / 100).toString(),
    );

    if (isDisabled) {
        return (
            <BodyMediumRegular>
                {formatDollars(editedCents / 100, false)}
            </BodyMediumRegular>
        );
    }

    if (isEditing) {
        const formattedDraft = formatWithCommas(draft);
        const displayValue = formattedDraft;
        const displayText = displayValue || placeholderValue;
        const digitCount = displayText.replace(/[^0-9]/g, '').length;
        const commaCount = displayText.replace(/[^,]/g, '').length;
        const charWidth = digitCount + commaCount * 0.4;

        const inputStyle: StyleProp<TextStyle> = {
            fontFamily: getDefaultFontFamily(),
            fontSize: 16,
            fontWeight: '400',
            lineHeight: 22,
            paddingVertical: 0,
            paddingHorizontal: 0,
            width: `${Math.max(charWidth, 1)}ch` as unknown as number,
        };

        return (
            <XStack alignItems="center" gap={0}>
                <BodyMediumRegular>$</BodyMediumRegular>
                <TextInput
                    ref={inputRef}
                    value={displayValue}
                    placeholder={placeholderValue}
                    placeholderTextColor="#999"
                    onChangeText={(text) => {
                        setDraft(text.replace(/[^0-9]/g, ''));
                    }}
                    onBlur={handleCommit}
                    onSubmitEditing={handleCommit}
                    keyboardType="number-pad"
                    style={inputStyle}
                    aria-label={`Edit ${goal.label} amount`}
                />
            </XStack>
        );
    }

    return (
        <BodyMediumRegular
            cursor="pointer"
            onPress={handleStartEdit}
            textDecorationLine="underline"
            textDecorationStyle="dashed"
            textDecorationColor="$gray200"
        >
            {formatDollars(editedCents / 100, false)}
        </BodyMediumRegular>
    );
};

// --- Mobile full-view editor ---

interface MobileEditViewProps {
    goal: SavingsGoalEstimateItem;
    currentCents: number;
    onSave: (newCents: number) => void;
    onBack: () => void;
}

const MobileEditView = ({
    goal,
    currentCents,
    onSave,
    onBack,
}: MobileEditViewProps) => {
    const [draft, setDraft] = useState('');
    const inputRef = useRef<TextInput>(null);

    const placeholderValue = formatWithCommas(
        currentCents > 0 ? Math.round(currentCents / 100).toString() : '0',
    );

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 100);
    }, []);

    const handleClear = useCallback(() => {
        setDraft('');
        inputRef.current?.focus();
    }, []);

    const handleSave = useCallback(() => {
        if (draft === '') {
            onSave(currentCents);
            return;
        }
        const dollars = parseInt(draft, 10);
        if (Number.isNaN(dollars) || dollars <= 0) {
            return;
        }
        onSave(dollars * 100);
    }, [draft, currentCents, onSave]);

    const draftDollars = draft === '' ? null : parseInt(draft, 10);
    const isSaveDisabled =
        draftDollars != null &&
        (Number.isNaN(draftDollars) || draftDollars <= 0);

    return (
        <YStack width="100%" gap={20}>
            <H5Regular>{goal.label}</H5Regular>

            <XStack
                width="100%"
                backgroundColor="$gray100"
                borderRadius={12}
                paddingHorizontal={16}
                paddingVertical={14}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack alignItems="center" flex={1}>
                    <BodyMediumRegular color="$gray1100">$ </BodyMediumRegular>
                    <TextInput
                        ref={inputRef}
                        value={formatWithCommas(draft)}
                        onChangeText={(text) => {
                            setDraft(text.replace(/[^0-9]/g, ''));
                        }}
                        onSubmitEditing={handleSave}
                        keyboardType="number-pad"
                        placeholder={placeholderValue}
                        placeholderTextColor="#999"
                        style={
                            {
                                fontFamily: getDefaultFontFamily(),
                                fontSize: 16,
                                fontWeight: '400',
                                lineHeight: 22,
                                flex: 1,
                                paddingVertical: 0,
                            } as const
                        }
                        aria-label={`Amount for ${goal.label}`}
                    />
                </XStack>
                {draft.length > 0 && (
                    <BodyMediumRegular
                        color="$black"
                        onPress={handleClear}
                        cursor="pointer"
                    >
                        Clear
                    </BodyMediumRegular>
                )}
            </XStack>

            <XStack width="100%" gap={12}>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Back"
                        width="full"
                        onPress={onBack}
                    />
                </YStack>
                <YStack flex={1}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        width="full"
                        disabled={isSaveDisabled}
                        onPress={handleSave}
                    />
                </YStack>
            </XStack>
        </YStack>
    );
};

// --- Main widget ---

export const SavingsGoalAmountsWidget = ({
    goals,
    confirmationPrefix,
    confirmationPayload,
    disabled = false,
    isActive = true,
    onConfirm,
}: SavingsGoalAmountsWidgetComponentProps) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const isWidgetDisabled = disabled || !isActive || hasSubmitted;

    const [editedAmounts, setEditedAmounts] = useState<Record<string, number>>(
        () => {
            const entries: Array<[string, number]> = goals.map((g) => [
                g.goalId,
                g.targetAmountCents,
            ]);
            return Object.fromEntries(entries);
        },
    );
    const [editingGoalId, setEditingGoalId] = useState<string | null>(null);

    const prevGoalsRef = useRef(goals);
    useEffect(() => {
        const prevById = new Map(
            prevGoalsRef.current.map((g) => [g.goalId, g.targetAmountCents]),
        );
        setEditedAmounts((prev) => {
            const next = { ...prev };
            for (const goal of goals) {
                const prevServer = prevById.get(goal.goalId);
                if (
                    prevServer == null ||
                    prevServer !== goal.targetAmountCents
                ) {
                    next[goal.goalId] = goal.targetAmountCents;
                }
            }
            const activeGoalIds = new Set(goals.map((g) => g.goalId));
            return Object.fromEntries(
                Object.entries(next).filter(([key]) => activeGoalIds.has(key)),
            );
        });
        prevGoalsRef.current = goals;
    }, [goals]);

    const effectiveGoals: SavingsGoalEstimateItem[] = useMemo(
        () =>
            goals.map((goal) => ({
                ...goal,
                targetAmountCents:
                    editedAmounts[goal.goalId] ?? goal.targetAmountCents,
            })),
        [goals, editedAmounts],
    );

    const handleCommitAmount = useCallback(
        (goalId: string, newCents: number) => {
            setEditedAmounts((prev) => ({ ...prev, [goalId]: newCents }));
            setEditingGoalId(null);
        },
        [],
    );

    const handleConfirm = useCallback(() => {
        if (isWidgetDisabled) {
            return;
        }

        const updatedPayload = {
            ...confirmationPayload,
            goals: effectiveGoals,
        };

        const agentPayload = buildDraftedMessageAgentPayload({
            label: 'Looks good',
            confirmationPrefix,
            confirmationPayload: updatedPayload,
        });

        setHasSubmitted(true);
        void onConfirm('Looks good', agentPayload);
    }, [
        confirmationPayload,
        confirmationPrefix,
        effectiveGoals,
        isWidgetDisabled,
        onConfirm,
    ]);

    const mobileEditingGoal = useMemo(
        () =>
            !isNonMobileWeb && editingGoalId != null
                ? effectiveGoals.find((g) => g.goalId === editingGoalId)
                : null,
        [editingGoalId, effectiveGoals],
    );

    if (!isNonMobileWeb && mobileEditingGoal != null) {
        return (
            <YStack
                width="100%"
                padding={20}
                backgroundColor="$white"
                borderRadius={20}
                gap={16}
                {...Shadows.SMALL}
            >
                <MobileEditView
                    goal={mobileEditingGoal}
                    currentCents={mobileEditingGoal.targetAmountCents}
                    onSave={(newCents) => {
                        handleCommitAmount(mobileEditingGoal.goalId, newCents);
                    }}
                    onBack={() => {
                        setEditingGoalId(null);
                    }}
                />
            </YStack>
        );
    }

    return (
        <EditHintTooltip>
            <YStack
                width="100%"
                padding={20}
                backgroundColor="$white"
                borderRadius={20}
                gap={8}
                {...Shadows.SMALL}
            >
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="baseline"
                >
                    <H6Regular>Your goals</H6Regular>
                </XStack>

                <YStack width="100%">
                    {effectiveGoals.map((goal) => (
                        <XStack
                            key={goal.goalId}
                            width="100%"
                            justifyContent="space-between"
                            alignItems="center"
                            paddingVertical={4}
                            {...(!isWidgetDisabled && {
                                onPress: () => {
                                    if (editingGoalId !== goal.goalId) {
                                        setEditingGoalId(goal.goalId);
                                    }
                                },
                                cursor: 'pointer',
                                ...(editingGoalId === goal.goalId && {
                                    onMouseDown: (e: {
                                        preventDefault: () => void;
                                    }) => {
                                        e.preventDefault();
                                    },
                                }),
                            })}
                        >
                            <BodyMediumRegular color="$gray1100">
                                {goal.label}
                            </BodyMediumRegular>
                            {isNonMobileWeb ? (
                                <View
                                    paddingHorizontal={8}
                                    paddingVertical={4}
                                    borderRadius={999}
                                    backgroundColor="$gray200"
                                >
                                    <EditableAmountCell
                                        goal={goal}
                                        editedCents={goal.targetAmountCents}
                                        isEditing={
                                            editingGoalId === goal.goalId
                                        }
                                        isDisabled={isWidgetDisabled}
                                        onStartEdit={() => {
                                            setEditingGoalId(goal.goalId);
                                        }}
                                        onCommit={(newCents) => {
                                            handleCommitAmount(
                                                goal.goalId,
                                                newCents,
                                            );
                                        }}
                                    />
                                </View>
                            ) : (
                                <View
                                    paddingHorizontal={8}
                                    paddingVertical={4}
                                    borderRadius={999}
                                    backgroundColor="$gray200"
                                >
                                    <BodyMediumRegular color="$gray1100">
                                        {formatDollars(
                                            goal.targetAmountCents / 100,
                                            false,
                                        )}
                                    </BodyMediumRegular>
                                </View>
                            )}
                        </XStack>
                    ))}
                </YStack>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Looks good"
                    disabled={isWidgetDisabled}
                    width="full"
                    onPress={handleConfirm}
                />
            </YStack>
        </EditHintTooltip>
    );
};
